import React, { useCallback, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { useHistory } from "react-router-dom";
import AccordionReact from "devextreme-react/accordion";
import { Button } from "devextreme-react/button";
import Toolbar, { Item } from "devextreme-react/toolbar";
import DataSource from "devextreme/data/data_source";
import { createStore } from "devextreme-aspnet-data-nojquery";
import { alert, confirm } from "devextreme/ui/dialog";
import notify from "devextreme/ui/notify";
import axios from "axios";
import { ItemKitProfileRoutes } from "../../../../const/constants";
import DataGrid, {
    Column,
    ColumnChooser,
    ColumnChooserSearch,
    Export,
    FilterPanel,
    FilterRow,
    HeaderFilter,
    Pager,
    Paging,
    Selection,
    Sorting,
    StateStoring,
    Toolbar as GridToolbar,
    Item as GridToolbarItem,
} from "devextreme-react/data-grid";
import FormField from "../../../../dynamicFormBuilder/FormField";
import GridLayoutManager from "../../../../gridLayoutCustomization/GridLayoutManager";
import { GridLayout } from "../../../../common/GridLayout";
import { useSearchState } from "../../../../common/SearchStatePersistence";
import { getApiErrorMessage, CONCURRENCY_HANDLED } from "../../../../common/concurrency";
import { MESSAGES } from "../../../../common/Messages";
import data from "../../../../assets/apiRoutes.json";
import { SearchLayout } from "./Layout";
import "../../../../common/css/searchPage.css";

// FIND screen for Delphi WMD0103 "WM Item Kit Data Profile" (browse of IMITEMKITTHEADERDP).
// Accordion search panel driven by the shared dynamic form, plus a results grid whose layout is
// saved per user via GridLayoutManager.
const ROUTES = data.ItemKitProfile;
const searchpanel = [{ ID: 1, Title: "Search Filters" }];
const allowedPageSizes = [10, 20, 100, 500, "all"];
const gridKey = "c93a4f18-6d27-4b50-91ae-58d3f027ab64";

// Rule 17: the search survives one round trip into the Add/Edit form and is dropped on any other
// navigation or a reload.
const SEARCH_STATE_PAGE_KEY = "wm.itemKitProfile";
const SEARCH_STATE_DETAIL_PATHS = [
    ItemKitProfileRoutes.Create,
    ItemKitProfileRoutes.Edit,
];

// One key per search-schema field so the controlled inputs stay controlled.
const buildInitialFields = () => {
    const init = {};
    SearchLayout.forEach((g) => g.fields.forEach((f) => { init[f.name] = ""; }));
    return init;
};

function List(props) {
    const accessToken = props.oidc?.user?.access_token;
    const currentUser = props.oidc?.user;
    const dynamicFormSetting = props.oidc?.user?.profile?.DYNAMICFORMSETTING;
    const history = useHistory();

    const dataGrid = useRef(null);
    const rowId = useRef("");
    const formRefs = useRef({});

    const [resultData, setResultData] = useState(null);
    const [selectedItems, setSelectedItems] = useState([]);
    const [searchPanelStatus, setSearchPanelStatus] = useState(false);
    const [isRowSelected, setIsRowSelected] = useState(false);
    const [currentLayout, setCurrentLayout] = useState("Default");

    const initialFormFields = useMemo(() => buildInitialFields(), []);

    // Grid *layout* is a durable preference and stays in the database; the *filter* is transient and
    // is handled by useSearchState (rule 17).
    const saveState = useCallback((state) => {
        GridLayout.sendStorageRequest(gridKey, "POST", accessToken, state, currentLayout);
    }, [accessToken, currentLayout]);
    const loadState = useCallback(() => {
        return GridLayout.sendStorageRequest(gridKey, "GET", accessToken, null, currentLayout);
    }, [accessToken, currentLayout]);

    const {
        formFields,
        setformFields,
        rememberSearch,
        forgetSearch,
        loadGridState,
        saveGridState,
    } = useSearchState({
        pageKey: SEARCH_STATE_PAGE_KEY,
        initialFormFields,
        layout: SearchLayout,
        detailPaths: SEARCH_STATE_DETAIL_PATHS,
        enabled: !props.pickerMode,
        gridRef: dataGrid,
        loadGridLayoutState: loadState,
        saveGridLayoutState: saveState,
        gridLayoutName: currentLayout,
    });

    // Built from the criteria handed in, not from a closure over formFields, so Reset can rebuild
    // the grid from the empty criteria without waiting for the state update to land.
    const loadDatasource = (fields) => {
        const criteria = fields || {};
        const store = createStore({
            key: "RowId",
            loadParams: {
                "profileFilter.ProfileCode": () => criteria.ProfileCode || "",
                "profileFilter.Description": () => criteria.Description || "",
                "profileFilter.Code1": () => criteria.Code1 || "",
                "profileFilter.Code2": () => criteria.Code2 || "",
                "profileFilter.Code3": () => criteria.Code3 || "",
                "profileFilter.Code4": () => criteria.Code4 || "",
                "profileFilter.Code5": () => criteria.Code5 || "",
            },
            loadUrl: `${window._env_.REACT_APP_APIS}/${ROUTES.GetItemKitProfiles}`,
            onBeforeSend: (method, ajaxOptions) => {
                ajaxOptions.headers = {
                    access_token: accessToken,
                    Authorization: `Bearer ${accessToken}`,
                };
            },
        });
        return new DataSource({ store });
    };

    const applySearch = useCallback((fields) => {
        setResultData(loadDatasource(fields));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accessToken]);

    // formFields already carries whatever was remembered from the previous visit, so the first pass
    // re-applies it — no extra fetch and no flash of unfiltered rows.
    React.useEffect(() => {
        applySearch(formFields);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const selectionChanged = useCallback((e) => {
        let newItems = [...selectedItems];
        e.removedItems.forEach((item) => { const i = newItems.indexOf(item); if (i >= 0) newItems.splice(i, 1); });
        if (e.addedItems.length) newItems = [...newItems, ...e.addedItems];
        setSelectedItems(newItems);
        setSearchPanelStatus((prev) => !prev);
    }, [selectedItems]);

    const handleSearch = (e) => {
        e.event.stopPropagation();
        applySearch(formFields);
        rememberSearch(formFields);
    };

    const handleReset = (e) => {
        e.event.stopPropagation();
        applySearch(forgetSearch());
    };

    const handleFieldChange = useCallback(() => {}, []);

    const onSelectionChangedGrid = ({ selectedRowsData }) => {
        const row = selectedRowsData && selectedRowsData.length === 1 ? selectedRowsData[0] : null;
        rowId.current = row ? row.RowId : "";
        setIsRowSelected(!!row);
    };

    const handleCreateClick = () => history.push(ItemKitProfileRoutes.Create);
    const handleEditClick = () => {
        if (!rowId.current) return;
        history.push(`${ItemKitProfileRoutes.Edit}?id=${rowId.current}`);
    };

    const handleDeleteClick = (e) => {
        // Sits on the accordion title bar: without this the click also collapses the search panel.
        if (e?.event) e.event.stopPropagation();
        if (!rowId.current) return;

        // Rule 12: project-standard confirmation wording.
        confirm(MESSAGES.DELETE_SELECTED_RECORD_CONFIRM, MESSAGES.CONFIRM).then((ok) => {
            if (!ok) return;

            axios.delete(
                `${window._env_.REACT_APP_APIS}/${ROUTES.DeleteItemKitProfile}?rowId=${rowId.current}`,
                { headers: { "Content-Type": "application/json", access_token: accessToken } },
            )
                .then((res) => {
                    // Rule 7: a successful delete is green, and the grid drops the row.
                    notify(res.data?.Message || MESSAGES.DELETE_SUCCESS, "success", 3000);
                    rowId.current = "";
                    setIsRowSelected(false);
                    if (dataGrid.current?.instance) dataGrid.current.instance().refresh();
                })
                .catch((err) => {
                    if (err?.[CONCURRENCY_HANDLED]) return;
                    // The "profile still used by a kit header" refusal arrives as a 400 and lands here.
                    alert(getApiErrorMessage(err, MESSAGES.ERROR), MESSAGES.ERROR);
                });
        });
    };

    const pager = useMemo(() => ({
        visible: true, showPageSizeSelector: true, allowedPageSizes, showNavigationButtons: true,
    }), []);

    return (
        <div className="search-page-container">
            <div className="search-page-content">
                <AccordionReact
                    dataSource={searchpanel}
                    collapsible={true}
                    multiple={false}
                    animationDuration={300}
                    selectedItems={selectedItems}
                    onSelectionChanged={(e) => { if (e.currentTarget === e.target) selectionChanged(e); }}
                    itemTitleRender={() => (
                        <Toolbar className="accordian-toolbar">
                            <Item location="before">
                                <div className="title">
                                    <h6 style={{ color: window._env_.Color2 }}>Enter Item Kit Profiles</h6>
                                </div>
                            </Item>
                            <Item location="after">
                                <Button className="accordianButton" onClick={handleSearch} text="Search" type="default" />
                            </Item>
                            <Item location="after">
                                <Button className="accordianButton" onClick={handleDeleteClick} text="Delete" disabled={!isRowSelected} />
                            </Item>
                            <Item location="after">
                                <Button className="accordianButton" onClick={handleReset} text="Reset" />
                            </Item>
                        </Toolbar>
                    )}
                    itemRender={() => (
                        <div>
                            <FormField
                                currrentUser={currentUser}
                                layoutId="0103a1f4-52b7-4c19-9f8d-0103aabbccdd"
                                layoutName="WMItemKitProfileSearchForm"
                                defaultFormSchema={SearchLayout}
                                formFields={formFields}
                                setformFields={setformFields}
                                handleFieldValueChange={handleFieldChange}
                                DYNAMICFORMSETTING={dynamicFormSetting}
                                formRefs={formRefs}
                                noSpacingBeforeFirstRow={true}
                            />
                        </div>
                    )}
                    id="accordion-container"
                    className="accordion-height"
                />

                <DataGrid
                    ref={dataGrid}
                    dataSource={resultData}
                    remoteOperations={true}
                    showBorders={true}
                    hoverStateEnabled={true}
                    allowColumnReordering={true}
                    allowColumnResizing={true}
                    columnAutoWidth={true}
                    className={searchPanelStatus ? "GridHeightAccordianOpen" : "GridHeightAccordianClose"}
                    onSelectionChanged={onSelectionChangedGrid}
                    onExporting={(e) => { if (window.handleDevExtremeDataGridExport) window.handleDevExtremeDataGridExport(e); }}
                >
                    <ColumnChooser enabled={true} title="Choose Column" width={290} height={290} sortOrder="asc">
                        <ColumnChooserSearch enabled={true} editorOptions={{ placeholder: "Search column" }} />
                    </ColumnChooser>
                    <StateStoring enabled={true} type="custom" customLoad={loadGridState} storageKey={gridKey} customSave={saveGridState} />
                    <Export enabled={true} allowExportSelectedData={true} />
                    <Selection mode="multiple" />
                    <Sorting mode="multiple" />
                    <FilterRow visible={true} />
                    <HeaderFilter visible={true} />
                    <FilterPanel visible={true} />
                    <Paging defaultPageSize={20} />
                    <Pager {...pager} />

                    {/* Rule 8: the technical key is hidden. Rule 10: Record # is exposed read-only. */}
                    <Column dataField="RowId" caption="Row Id" visible={false} showInColumnChooser={false} />
                    <Column dataField="RecordNumber" caption="Record #" dataType="string" />
                    <Column dataField="ProfileCode" caption="Profile" dataType="string" />
                    <Column dataField="Description" caption="Description" dataType="string" />
                    <Column dataField="Code1" caption="Code 1" dataType="string" visible={false} />
                    <Column dataField="Code2" caption="Code 2" dataType="string" visible={false} />
                    <Column dataField="Code3" caption="Code 3" dataType="string" visible={false} />
                    <Column dataField="Code4" caption="Code 4" dataType="string" visible={false} />
                    <Column dataField="Code5" caption="Code 5" dataType="string" visible={false} />
                    <Column dataField="Comment" caption="Comment" dataType="string" visible={false} />

                    <GridToolbar>
                        <GridToolbarItem location="before">
                            <Button onClick={handleEditClick} text="Edit" type="default" disabled={!isRowSelected} />
                        </GridToolbarItem>
                        <GridToolbarItem location="before">
                            <Button onClick={handleCreateClick} text="Create" type="default" className="gridTopButton" />
                        </GridToolbarItem>
                        <GridToolbarItem location="after">
                            <GridLayoutManager gridKey={gridKey} dataGrid={dataGrid} setCurrentLayout={setCurrentLayout} />
                        </GridToolbarItem>
                        <GridToolbarItem name="exportButton" />
                        <GridToolbarItem name="columnChooserButton" />
                    </GridToolbar>
                </DataGrid>
            </div>
        </div>
    );
}

function mapStateToProps(state) {
    return { oidc: state.oidc };
}

export default connect(mapStateToProps)(List);
